import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../auth/auth.api';
import { TSuccessResponse } from '@/types/general/TSuccessResponse';
import TKPI from '@/types/kpi-management/TKPI';
import TPaginationParams from '@/types/general/TPaginationParams';
import TEmployeeKPI from '@/types/kpi-management/TEmployeeKPI';

export const kpiApi = createApi({
    reducerPath: 'kpiApi',
    tagTypes: ["KPI"],
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => ({
        getAllKpi: builder.query<
            TSuccessResponse<TKPI[]>, 
            { pagination?: TPaginationParams, searchKey?: string, filter?: { position_id: number|string, departments?: number[] } }
        >({
            query: ({ pagination = {} as any, searchKey, filter }) => {
                const params: Record<any, any> = {};
                const { page = 1, limit = 100 } = pagination;

                if (page && limit) {
                    params.page = page;
                    params.limit = limit;
                }

                if (searchKey) {
                    params.search = searchKey;
                }

                if (filter?.position_id) {
                    params.position_id = filter.position_id;
                }

                if (filter?.departments && filter.departments.length > 0) {
                    params.departments = filter.departments.join(", ");
                }

                return {
                    url: "kpi-management/kpi",
                    params: params
                }
            }
        }),

        getAllKpiByFilter: builder.query<
            TSuccessResponse<TKPI[]>, 
            { position_id: number|string, kra_detail_id?: number | null }
        >({
            query: ({ position_id, kra_detail_id }) => {
                const params: Record<any, any> = {};

                if (position_id) {
                    params.position_id = position_id;
                }

                if (kra_detail_id) {
                    params.kra_detail_id = kra_detail_id;
                }

                return {
                    url: `kpi-management/kpis`,
                    params: params
                }
            }
        }),

        getEmployeeKPI: builder.query<
            TSuccessResponse<TEmployeeKPI[]>, 
            { year: string | number; month: string | number }
        >({
            query: ({ year = new Date().getFullYear(), month = (new Date().getMonth()) + 1 }) => {
                return {
                    url: "kpi-management/kpi",
                    params: { year, month }
                }
            }
        }),

        createKPI: builder.mutation<
            TSuccessResponse<null>, 
            TKPI
        >({
            query: (body) => ({
                url: 'kpi-management/kpi',
                method: 'POST',
                body,
            }),
        }),

        updateKPI: builder.mutation<
            TSuccessResponse<null>, 
            { kpiId: number, data: TKPI }
        >({
            query: ({ kpiId, data }) => ({
                url: `kpi-management/kpi/${kpiId}`,
                method: 'PATCH',
                body: data,
            }),
        }),

        getKPI: builder.query<
            TSuccessResponse<TKPI[]>, 
            { positionId: number; kraCode: string }
        >({
            query: ({ positionId, kraCode }) => `kpi-management/kpi/${positionId}/${kraCode}`,
        }),

        deleteKPI: builder.mutation<
            TSuccessResponse<null>, 
            string
        >({
            query: (kpiId) => ({
                url: `kpi-management/kpi/${kpiId}`,
                method: 'DELETE',
            }),
        }),
    }),
});

export const {
    useGetAllKpiQuery,
    useGetKPIQuery,
    useCreateKPIMutation,
    useUpdateKPIMutation,
    useDeleteKPIMutation,
    useGetEmployeeKPIQuery,
    useLazyGetAllKpiByFilterQuery
} = kpiApi;
